import React from 'react';
import { Container, Typography, Paper, Box, Grid, Button, Card, CardContent, CircularProgress, Divider } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';       // Performans ikonu
import AssessmentIcon from '@mui/icons-material/Assessment';           // Değerlendirme ikonu
import TrackChangesIcon from '@mui/icons-material/TrackChanges';       // Hedef Takibi ikonu
import RateReviewIcon from '@mui/icons-material/RateReview';           // Geri Bildirim ikonu
import { useTranslation } from 'react-i18next';

const PerformanceManagementPage: React.FC = () => {
  const { t } = useTranslation(['humanResources', 'common2']);
  const hr = (key: string, defaultValue: string) => t(key, { ns: 'humanResources', defaultValue });

  return (
    <Box sx={{ 
      width: '100%', 
      height: '100%', 
      position: 'relative',
      overflowX: 'hidden'
    }}>
      <Box 
        sx={{ 
          height: 'calc(100vh - 80px)', 
          overflowY: 'auto', 
          overflowX: 'hidden',
          width: '100%',
          paddingRight: '10px',
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': {
            width: '8px',
            marginLeft: '10px'
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '10px'
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '10px',
            '&:hover': {
              background: '#555'
            }
          }
        }}
      >
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <Box sx={{ pb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <TrendingUpIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
              <Typography variant="h4" component="h1">
                {hr('pages.performance.title', 'Performance Management System')}
              </Typography>
            </Box>
            
            <Typography variant="subtitle1" color="textSecondary" sx={{ mb: 4 }}>
              {hr('pages.performance.subtitle', 'This page is under construction. The performance management module will be available soon.')}
            </Typography>

            <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                {hr('pages.shared.developmentProcess', 'Development Process')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ my: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6">{hr('pages.shared.analysis', 'Analysis')}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                          <CircularProgress variant="determinate" value={100} color="success" />
                          <Typography variant="body2" sx={{ ml: 2 }}>{hr('pages.shared.completed', 'Completed')}</Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6">{hr('pages.shared.design', 'Design')}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                          <CircularProgress variant="determinate" value={25} color="primary" />
                          <Typography variant="body2" sx={{ ml: 2 }}>{hr('pages.shared.inProgress', 'In Progress')}</Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6">{hr('pages.shared.development', 'Development')}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                          <CircularProgress variant="determinate" value={5} color="warning" />
                          <Typography variant="body2" sx={{ ml: 2 }}>{hr('pages.shared.initialStage', 'Initial Stage')}</Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            </Paper>

            <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                {hr('pages.shared.plannedFeatures', 'Module Features (Planned)')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <AssessmentIcon sx={{ fontSize: 40, mb: 1, color: 'info.main' }} />
                      <Typography variant="h6" gutterBottom>
                        {hr('pages.performance.features.evaluationsTitle', 'Performance Evaluations')}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {hr('pages.performance.features.evaluationsDesc', 'Management of periodic evaluation processes, forms, and result tracking.')}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <TrackChangesIcon sx={{ fontSize: 40, mb: 1, color: 'success.main' }} />
                      <Typography variant="h6" gutterBottom>
                        {hr('pages.performance.features.goalTrackingTitle', 'Goal Tracking (OKR/KPI)')}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {hr('pages.performance.features.goalTrackingDesc', 'Definition of individual and team goals, progress tracking, and reporting.')}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <RateReviewIcon sx={{ fontSize: 40, mb: 1, color: 'secondary.main' }} />
                      <Typography variant="h6" gutterBottom>
                        {hr('pages.performance.features.feedbackTitle', 'Feedback Mechanisms')}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {hr('pages.performance.features.feedbackDesc', 'Management and analysis of 360-degree or regular feedback processes.')}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Paper>

            <Paper elevation={3} sx={{ p: 3, mb: 4, bgcolor: 'rgba(21, 101, 192, 0.04)' }}>
              <Typography variant="h6" gutterBottom>
                {hr('pages.performance.whyTitle', 'Why a Performance Management System?')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Typography variant="body1" paragraph>
                {hr('pages.performance.whyIntro', 'Systematic performance management directly impacts employee motivation and company success. With this system:')}
              </Typography>
              
              <Box component="ul" sx={{ pl: 2 }}>
                <Box component="li" sx={{ mb: 1 }}>
                  <Typography variant="body1">{hr('pages.performance.benefits.strengths', 'Employees\' strengths and development areas are identified.')}</Typography>
                </Box>
                <Box component="li" sx={{ mb: 1 }}>
                  <Typography variant="body1">{hr('pages.performance.benefits.fairness', 'A fair and transparent evaluation environment is provided.')}</Typography>
                </Box>
                <Box component="li" sx={{ mb: 1 }}>
                  <Typography variant="body1">{hr('pages.performance.benefits.careerPlans', 'Career development plans are supported.')}</Typography>
                </Box>
                <Box component="li" sx={{ mb: 1 }}>
                  <Typography variant="body1">{hr('pages.performance.benefits.alignment', 'Company goals and individual goals are aligned.')}</Typography>
                </Box>
                <Box component="li" sx={{ mb: 1 }}>
                  <Typography variant="body1">{hr('pages.performance.benefits.recognition', 'High-performing employees are recognized and motivated.')}</Typography>
                </Box>
              </Box>
              
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                {hr('pages.performance.conclusion', 'Our performance management module will contribute to your company\'s growth by simplifying evaluation processes, centralizing goal tracking, and supporting a feedback culture.')}
              </Typography>
            </Paper>

            <Box sx={{ textAlign: 'center', mt: 4, pb: 4 }}>
              <Button variant="contained" color="primary" disabled>
                {hr('pages.shared.moduleAvailableWhenReady', 'The Module Will Be Available When Ready')}
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default PerformanceManagementPage; 